import { useQuery } from "@apollo/client/react";
import { styled } from "@mui/material";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { UPLOAD_QUERY_WITH_DOWNLOADS } from "../../graphql";
import { ALL_UPLOADS_QUERY_WITH_DOWNLOADS } from "../../graphql/queries/uploads";
import type {
  AllUploadDownloadsQuery,
  InputsFragmentFragment,
  Upload,
  UploadDownloadsQuery,
} from "../../graphql/types";
import {
  DownloadBundle,
  DownloadDocument,
  DownloadQuery,
  DownloadQueryVariables,
  FlowDownloadsDocument,
  FlowDownloadsQuery,
  FlowDownloadsQueryVariables,
  UploadForDownloadsDocument,
  UploadForDownloadsQuery,
  UploadForDownloadsQueryVariables,
} from "../../graphql/types";
import { DownloadTable } from "../DownloadTable";
import { UploadTable } from "../UploadTable";
import { EmptyContainer } from "../ui/EmptyContainer";
import { SkeletonTable } from "../ui/skeletons";
import { QueryResult } from "./QueryResult";

const DOWNLOADS_PAGE_SIZE = 10;

const Container = styled("div")({
  position: "relative",
});
const InfoSectionHeader = styled("div")({
  fontSize: "13px",
  fontStyle: "normal",
  padding: "10px 0px 10px 10px",
});

const InfoSectionTitle = styled("span")`
  margin-left: 10px;
`;

const DetailContainerRow = styled("tr")(({ theme }) => ({
  "> td": {
    wordBreak: "break-word",
    padding: "0px",
    border: "none",
    "> div": {
      padding: "10px 20px",
      backgroundColor: theme.palette.surfaceLight40.main,
      border: "none",
      borderRadius: " 0 0 11px 11px",
    },
  },
}));

const DownloadDetail = ({ download_id }: { download_id: string }) => {
  const variables = { id: download_id };

  const { loading, error, data } = useQuery<DownloadQuery, DownloadQueryVariables>(DownloadDocument, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    variables,
  });

  return (
    <Container>
      <InfoSectionHeader>
        <CloudArrowDownIcon />
        <InfoSectionTitle>Downloads</InfoSectionTitle>
      </InfoSectionHeader>
      <QueryResult<DownloadBundle, DownloadQuery>
        data={data}
        error={error}
        loading={loading}
        emptyElement={<EmptyContainer small={true} />}
        loadingElement={<SkeletonTable rows={2} />}
        resultsMapper={(bundle) => <DownloadTable bundles={[bundle]} />}
        nodeName={"download"}
      />
    </Container>
  );
};

const DownloadsForFlow = ({ flow_id }: { flow_id: string }) => {
  const variables = { id: flow_id };

  const { loading, error, data } = useQuery<FlowDownloadsQuery, FlowDownloadsQueryVariables>(FlowDownloadsDocument, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables,
  });
  return (
    <Container>
      <InfoSectionHeader>
        <CloudArrowDownIcon />
        <InfoSectionTitle>Downloads</InfoSectionTitle>
      </InfoSectionHeader>
      <QueryResult<DownloadBundle[], FlowDownloadsQuery>
        data={data}
        error={error}
        loading={loading}
        emptyElement={<EmptyContainer small={true} />}
        loadingElement={<SkeletonTable rows={2} />}
        resultsMapper={(rows) => <DownloadTable bundles={rows} />}
        nodeName={"flow_downloads"}
      />
    </Container>
  );
};

type InputBundle = InputsFragmentFragment["download"] & {
  sortKey: string;
  parts: InputsFragmentFragment[];
};

const sort_downloads = (download_bundles: InputBundle[]) => {
  return download_bundles.sort((s1, s2) => {
    return s1.sortKey > s2.sortKey ? 1 : -1;
  });
};

const toBundles = (download_parts?: (InputsFragmentFragment | null)[] | null) => {
  // In this GQL query we request the data (Part -> Download Bundle).  DownloadTable requires (Download Bundle -> Parts).
  // Therefore convert to that shape.
  const bundles = (download_parts ?? [])
    .filter((part) => part !== null)
    .map((part) => {
      return Object.assign(
        {},
        {
          sortKey: `${part.source_table.project}:${part.source_table.name}`,
          parts: [part],
        },
        part.download
      );
    });
  return sort_downloads(bundles) as unknown as DownloadBundle[];
};

type UploadWithInputs = NonNullable<UploadDownloadsQuery["upload"]>;
type UploadNode = NonNullable<AllUploadDownloadsQuery["uploads"]>["edges"][number]["node"];

/** `rows` is a list of uploads under `all_upload_downloads`, but a single one under `upload_downloads`. */
const Upload = ({ rows, showUploads }: { rows: UploadNode[] | UploadWithInputs | null; showUploads: boolean }) => {
  //prettier-ignore
  const bundles = rows
    ? toBundles(
        Array.isArray(rows)
          ? rows.length
            ? rows[0].inputs
            : []
          : rows.inputs
      )
    : [];

  const uploads = (Array.isArray(rows) && rows.length > 0 ? [rows[0]] : []) as unknown as Upload[];
  return (
    <div style={{ display: "grid", gap: "20px" }}>
      {showUploads ? (
        <div>
          <InfoSectionHeader>
            <CloudArrowUpIcon />
            <InfoSectionTitle>Upload</InfoSectionTitle>
          </InfoSectionHeader>
          <UploadTable uploads={uploads} allowViewDownloads={false} />
        </div>
      ) : null}
      <div>
        <InfoSectionHeader>
          <CloudArrowDownIcon />
          <InfoSectionTitle>Downloads</InfoSectionTitle>
        </InfoSectionHeader>
        <DownloadTable bundles={bundles} />
      </div>
    </div>
  );
};

const DownloadsForUpload = ({
  upload_id,
  first = DOWNLOADS_PAGE_SIZE,
  showUploads = false,
}: {
  upload_id: string;
  first?: number;
  showUploads?: boolean;
}) => {
  const variables = { id: upload_id, first };
  const { loading, error, data } = useQuery(
    showUploads ? ALL_UPLOADS_QUERY_WITH_DOWNLOADS : UPLOAD_QUERY_WITH_DOWNLOADS,
    {
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-and-network",
      variables,
    }
  );
  return (
    <Container>
      <QueryResult
        data={data}
        error={error}
        loading={loading}
        emptyElement={<EmptyContainer small={true} />}
        loadingElement={<SkeletonTable rows={2} />}
        resultsMapper={(rows: UploadNode[] | UploadWithInputs | null) => (
          <Upload rows={rows} showUploads={showUploads} />
        )}
        nodeName={showUploads ? "uploads" : "upload"}
      />
    </Container>
  );
};

const UploadsForDownload = ({
  download_part_id,
  first = DOWNLOADS_PAGE_SIZE,
}: {
  download_part_id: string;
  first?: number;
}) => {
  const variables = { download_part_id, first };
  const { loading, error, data } = useQuery<UploadForDownloadsQuery, UploadForDownloadsQueryVariables>(
    UploadForDownloadsDocument,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      variables,
    }
  );
  return (
    <Container>
      <InfoSectionHeader>
        <CloudArrowUpIcon />
        <InfoSectionTitle>Uploads</InfoSectionTitle>
      </InfoSectionHeader>
      <QueryResult<Upload[], UploadForDownloadsQuery>
        data={data}
        error={error}
        loading={loading}
        emptyElement={<EmptyContainer small={true} />}
        loadingElement={<SkeletonTable rows={2} />}
        resultsMapper={(rows) => <UploadTable uploads={rows} allowViewDownloads={false} />}
        nodeName={"uploads"}
      />
    </Container>
  );
};

export { DetailContainerRow, DownloadDetail, DownloadsForFlow, DownloadsForUpload, toBundles, UploadsForDownload };
